import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const REQUIREMENTS = [
  "Start a Business", "Startup Development", "Registration & Certification", "Branding",
  "Website", "Mobile App", "E-commerce", "Technology", "Marketing",
  "Business Consulting", "Partnership", "Investment Discussion", "Other",
];
const CONTACT_METHODS = ["Call", "WhatsApp", "Email"];
const SOURCES = ["Google Search", "Social Media", "Referral", "Event", "News / Blog", "Other"];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", company: "", requirement: "", message: "",
    preferred_contact: "Call", source: "", consent: false, website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox to continue");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="contact-hero"
        kicker="Contact LaunchLab"
        title="LET'S BUILD SOMETHING"
        highlight="GREAT TOGETHER."
        subtext="Tell us about your idea or business — our team will get back to you."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-14 text-center"
            data-testid="enquiry-success"
          >
            <CheckCircle2 className="w-14 h-14 text-[#00E676] mx-auto mb-5" />
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white">
              THANK YOU. <span className="text-gradient-saffron">YOUR ENQUIRY HAS BEEN RECEIVED.</span>
            </h2>
            <p className="mt-4 text-slate-400">Our business team will reach out to you shortly.</p>
            <Button
              onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", company: "", requirement: "", message: "", preferred_contact: "Call", source: "", consent: false, website: "" }); }}
              data-testid="enquiry-another"
              className="mt-8 bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold"
            >
              Send Another Enquiry
            </Button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            onSubmit={onSubmit}
            className="glass rounded-2xl p-6 sm:p-10 space-y-6"
            data-testid="enquiry-form"
          >
            {/* honeypot */}
            <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name *</Label>
                <Input data-testid="enquiry-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Mobile Number *</Label>
                <Input data-testid="enquiry-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Email *</Label>
                <Input data-testid="enquiry-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Company / Business Name</Label>
                <Input data-testid="enquiry-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="bg-[#06080E] border-white/10" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Requirement *</Label>
                <Select value={form.requirement} onValueChange={(v) => set("requirement", v)} required>
                  <SelectTrigger data-testid="enquiry-requirement" className="bg-[#06080E] border-white/10 text-slate-200">
                    <SelectValue placeholder="What do you need?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-64">
                    {REQUIREMENTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">How did you hear about LaunchLab?</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="enquiry-source" className="bg-[#06080E] border-white/10 text-slate-200">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Message *</Label>
              <Textarea data-testid="enquiry-message" rows={5} required value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your idea, business or requirement..." className="bg-[#06080E] border-white/10" />
            </div>

            <div className="space-y-3">
              <Label className="text-slate-300">Preferred Contact Method</Label>
              <div className="flex gap-2.5" data-testid="enquiry-contact-method">
                {CONTACT_METHODS.map((m) => (
                  <button
                    type="button"
                    key={m}
                    data-testid={`enquiry-method-${m.toLowerCase()}`}
                    onClick={() => set("preferred_contact", m)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      form.preferred_contact === m ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300 hover:text-white"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="enquiry-consent-label">
              <input
                type="checkbox"
                data-testid="enquiry-consent"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                className="mt-1 w-4 h-4 rounded accent-[#FF8800]"
              />
              <span className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                I consent to LaunchLab contacting me regarding my enquiry via call, WhatsApp or email.
              </span>
            </label>

            <Button type="submit" disabled={sending} data-testid="enquiry-submit" className="w-full h-12 bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm tracking-wide">
              <Send className="w-4 h-4 mr-2" />
              {sending ? "SENDING..." : "SEND ENQUIRY"}
            </Button>
          </motion.form>
        )}
      </section>
      <Footer />
    </div>
  );
}
